import { z } from 'zod';

import { DevicePlatformEnum } from './filterMappings.js';
import { RowLevelEnum } from './rowLevel.js';

export const CompatibilityEnum = z.enum([
  'DISPLAY',
  'DISPLAY_INTERSTITIAL',
  'APP',
  'APP_INTERSTITIAL',
  'IN_STREAM_VIDEO',
  'IN_STREAM_AUDIO',
]);

export const AdTypeEnum = z.enum([
  'AD_SERVING_STANDARD_AD',
  'AD_SERVING_DEFAULT_AD',
  'AD_SERVING_CLICK_TRACKER',
  'AD_SERVING_TRACKING',
  'AD_SERVING_BRAND_SAFE_AD',
]);

export const PlacementActivityStatusEnum = z.enum([
  'PLACEMENT_STATUS_UNKNOWN',
  'PLACEMENT_STATUS_ACTIVE',
  'PLACEMENT_STATUS_INACTIVE',
  'PLACEMENT_STATUS_ARCHIVED',
  'PLACEMENT_STATUS_PERMANENTLY_ARCHIVED',
]);

export const AssetTypeEnum = z.enum(['FLASH', 'IMAGE', 'HTML_IMAGE', 'VIDEO']);

export const CampaignManagerAssetSchema = z.object({
  name: z.string().nullish(),
  externalId: z.string().nullish(),
  type: AssetTypeEnum,
  mimetype: z.string().nullish(),
  width: z.number().int().nullish(),
  height: z.number().int().nullish(),
});

export const CampaignManagerCreativeSchema = z.object({
  name: z.string(),
  externalId: z.string().nullish(),
  clickThroughUrl: z.string().nullish(),
  compatibility: z.array(CompatibilityEnum).default([]),
  width: z.number().int().nullish(),
  height: z.number().int().nullish(),
  assets: z.array(CampaignManagerAssetSchema).default([]),
});

export const CampaignManagerAdSchema = z.object({
  name: z.string(),
  campaignId: z.string().nullish(),
  externalId: z.string().nullish(),
  type: AdTypeEnum,
  compatibility: CompatibilityEnum.nullish(),
  width: z.number().int().nullish(),
  height: z.number().int().nullish(),
  creatives: z.array(CampaignManagerCreativeSchema).default([]),
});

export const AdformMemberSchema = z.object({
  name: z.string(),
  externalId: z.string().nullish(),
  width: z.number().int().nullish(),
  height: z.number().int().nullish(),
  campaignId: z.string().nullish(),
  externalUuid: z.string().nullish(),
  isActive: z.boolean().nullish(),
  adType: z.number().int().nullish(),
  contentType: z.number().int().nullish(),
  landingPageUrl: z.string().nullish(),
});

export const TrackerMappingExtraSchema = z.object({
  // Campaign Manager specific fields
  campaignManagerAdvertiserId: z.string().nullish(),
  campaignManagerCampaignId: z.string().nullish(),
  campaignManagerSiteId: z.string().nullish(),
  campaignManagerActiveStatus: PlacementActivityStatusEnum.nullish(),
  campaignManagerCompatibility: CompatibilityEnum.nullish(),
  campaignManagerAds: z.array(CampaignManagerAdSchema).default([]),
  // Appsflyer specific fields
  appsflyerMediaSourceId: z.string().nullish(),
  appsflyerDevicePlatform: DevicePlatformEnum.nullish(),
  // Adform specific fields
  adformCampaignId: z.string().nullish(),
  adformExternalUuid: z.string().nullish(),
  adformIsRtb: z.boolean().nullish(),
  adformIsActive: z.boolean().nullish(),
  adformAdType: z.number().int().nullish(),
  adformContentType: z.number().int().nullish(),
  adformTagId: z.string().nullish(),
  adformTagType: z.string().nullish(),
  adformBaseLineItemId: z.string().nullish(),
  adformLandingPageUrl: z.string().nullish(),
  adformMembers: z.array(AdformMemberSchema).default([]),
  // Size fields
  width: z.number().int().nullish(),
  height: z.number().int().nullish(),
  // Taxonomy specific fields
  taxonomyCampaignId: z.string().nullish(),
  taxonomyCampaignExternalId: z.string().nullish(),
  taxonomyAdGroupId: z.string().nullish(),
  taxonomyAdGroupExternalId: z.string().nullish(),
  taxonomyAdId: z.string().nullish(),
  taxonomyAdExternalId: z.string().nullish(),
});

export const TrackerMappingSchema = z.object({
  id: z.string(),
  settingsId: z.string(),
  filterMappingId: z.string().nullish(),
  filterMappingRelationshipId: z.string().nullish(),

  name: z.string(),
  externalId: z.string().nullable(),
  isPushAllowed: z.boolean(),

  mediaRowId: z.string(),
  taxonomyItemId: z.string(),
  taxonomyItemExternalId: z.string(),

  extra: TrackerMappingExtraSchema,

  createdAt: z.string(),
  updatedAt: z.string(),
});

export const ExportTagsRequestSchema = z.object({
  settingsId: z.string(),
  rowLevel: RowLevelEnum.nullable(),
  rowLevelId: z.string().nullable(),
  rowLevelExternalId: z.string().nullable(),
  trackerMappingId: z.string().nullable(),
  userTimezone: z.string(),
});
